/** ANGULAR MODULES */
import { NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { environment } from '../environments/environment';

/** CUSTOM MODULES */
import { SidebarModule } from './modules/core/sidebar/sidebar.module';
import { LibModule } from './lib/lib.module';
import { NavbarModule } from './modules/core/navbar/navbar.module';
import { MatNativeDateModule } from '@angular/material/core';
/** COMPONENTS */
import { AppComponent } from './app.component';
import { AuthenticationLayoutComponent } from './layouts/authentication/authentication-layout.component';
import { MainLayoutComponent } from './layouts/main/main-layout.component';

import { SpinnerComponent } from './shared/components/spinner/spinner.component';

/** GUARDS */
import { AuthenticationGuard } from './shared/guards/authentication.guard';

/** ROUTES */
import { AppRoutingModule } from './app-routing.module';

/** SERVICES */
import { SpinnerInterceptor } from './shared/services/spinner-interceptor';
import { AuthInterceptor } from './shared/services/auth-interceptor';
import { MsalErrorHandlerService } from './shared/services/msal-error-handler.service';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatMenuModule } from '@angular/material/menu';

import { ScrollingModule } from '@angular/cdk/scrolling';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { MessageService } from 'primeng/api';
import { ScrollTopModule } from "primeng/scrolltop";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ToastrModule } from 'ngx-toastr';

/** AZURE AD */
import { MsalModule, MsalRedirectComponent, MsalGuard, MsalInterceptor } from "@azure/msal-angular";
import { PublicClientApplication, InteractionType } from "@azure/msal-browser";
import { RoleGuard } from './shared/guards/role.guard';
import { BedSizeComponent } from './accommodation-unit/components/bed-size/bed-size/bed-size.component';

const isIE =
  window.navigator.userAgent.indexOf("MSIE ") > -1 ||
  window.navigator.userAgent.indexOf("Trident/") > -1;


@NgModule({
  declarations: [
    /** MAIN */
    AppComponent,
    AuthenticationLayoutComponent,
    MainLayoutComponent,

    /** SHARED */
    SpinnerComponent,
    BedSizeComponent,
  ],
  imports: [
    AppRoutingModule,
    ScrollingModule,
    SidebarModule,
    LibModule,
    NavbarModule,
    MatNativeDateModule,
    HttpClientModule, FormsModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatSidenavModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    CommonModule,
    BrowserAnimationsModule,
    HttpClientModule,
    // Registered at the root, not in accommodation-unit.module.ts (a lazy-loaded
    // module): PrimeNG's DialogService creates dynamic dialog components (e.g.
    // AutPropertyDataDialogComponent) using the root injector, which doesn't see
    // providers registered only inside a lazy module's own injector - those
    // components' ToastrService injection would throw NullInjectorError for
    // ToastConfig otherwise.
    ToastrModule.forRoot(),
    MsalModule.forRoot(
      new PublicClientApplication({
        auth: {
          clientId: environment.clientId,
          authority: environment.authority,
          redirectUri: environment.redirectUri,
          postLogoutRedirectUri: environment.postLogoutRedirectUri,
        },
        cache: {
          cacheLocation: "localStorage",
          storeAuthStateInCookie: isIE,  // Set to true for Internet Explorer 11
        },
      }),
      {
        interactionType: InteractionType.Redirect, // MSAL Guard Configuration
        authRequest: {
          scopes: ["user.read", environment.apiScope],
        },
      },
      {
        interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
        protectedResourceMap: new Map([
          ["https://graph.microsoft.com/v1.0/me", ["user.read"]],
          [environment.baseUrl, [environment.apiScope]],
        ]),
      }
    ),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    AuthenticationGuard,
    RoleGuard,
    // MessageService must be provided at root level because PrimeNG's DialogService
    // creates dynamic dialog components using the root injector, which doesn't see
    // providers registered only inside lazy-loaded modules
    MessageService
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule { }
